import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { onboardingPath } from './onboardingRoutes';
import { AppInfoTooltip } from '../../components/AppInfoTooltip';

interface PlanOutcome {
  code: string;
  displayName: string;
  description: string;
}

interface PlanApp {
  appId: string;
  displayName: string;
  /**
   * Phase 2.x-c PR-B — the pricing-summary endpoint returns the
   * mode-resolved Application Registry v2 copy directly. No client-side
   * join required.
   */
  subtitle?: string;
  description?: string;
  /**
   * Phase 2.x-c PR-C3b — outcome → app traceability. Every selected
   * outcome whose FOCUS_REGISTRY[outcome].apps array includes this
   * app. Used to render "Activated by: {outcomes}" beneath the chip so users
   * see which selection activated each app. Wire-optional so older
   * backends degrade to a flat list.
   */
  activatedByOutcomes?: string[];
  order: number;
}

/**
 * Phase 2.x-c PR-C3a — the plan the user will actually be on after
 * completion. Populated by the same `computePlanForOutcomes` call
 * `completeOnboarding` runs, so what the pricing screen shows is what
 * the subscription writes.
 */
interface PlanCatalogEntry {
  code: string;
  name: string;
  description: string;
  monthlyPriceCents: number;
  annualPriceCents: number | null;
  baseCredits: number;
  includedSeats: number;
  perSeatCents: number;
  maxSeats: number | null;
  storageLimitBytes: number;
}

interface PlanSummary {
  status: 'ready' | 'missing-outcomes';
  mode: 'business' | 'personal';
  workspaceName?: string;
  selectedOutcomes?: PlanOutcome[];
  apps?: PlanApp[];
  pricing?: {
    monthlyBaseCents: number;
    totalCredits: number;
    trialDays: number;
    isFree: boolean;
    /** PR-C3a: plan the user will be on after completion. */
    plan?: PlanCatalogEntry;
    /** PR-C3a: plan.baseCredits + Σ outcome.includedCredits. */
    monthlyIncludedCredits?: number;
  };
}

/** Human-readable storage label from bytes. */
function formatStorage(bytes: number): string {
  if (bytes >= 1024 ** 4) return `${(bytes / 1024 ** 4).toFixed(bytes % (1024 ** 4) === 0 ? 0 : 1)} TB`;
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(bytes % (1024 ** 3) === 0 ? 0 : 1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(0)} MB`;
  return `${bytes} B`;
}

/** Human-readable dollar amount from integer cents (no trailing .00). */
function formatDollars(cents: number): string {
  return cents % 100 === 0 ? `$${(cents / 100).toFixed(0)}` : `$${(cents / 100).toFixed(2)}`;
}

/**
 * Phase 2.x-c PR-C3b — map outcome codes to their displayNames.
 * Returned array is de-duplicated and preserves the order the codes
 * appear in `activatedByOutcomes` (which itself follows the user's
 * outcome-selection order via getOnboardingPlanSummary). Codes with
 * no matching displayName are skipped — the user won't recognize
 * "revenue" as prose.
 */
function outcomeNamesFor(
  activatedByOutcomes: readonly string[],
  outcomeNameByCode: Map<string, string>,
): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  for (const code of activatedByOutcomes) {
    const name = outcomeNameByCode.get(code);
    if (name && !seen.has(name)) {
      seen.add(name);
      names.push(name);
    }
  }
  return names;
}

type PageStatus = 'loading' | 'ready' | 'missing-outcomes' | 'error';

/** Capacity/activation-framed copy — pricing as what gets switched on. */
const MODE_COPY: Record<'business' | 'personal', { heading: string; sub: string }> = {
  business: {
    heading: 'Activate your business workspace',
    sub: 'Here is the operating system we assemble for your growth, operations, team, and clients.',
  },
  personal: {
    heading: 'Set up your personal space',
    sub: 'Here is everything we set up to help you organize your home, goals, and life.',
  },
};

/**
 * PricingSummary page — the onboarding pricing/activation step.
 *
 * Sources entirely from `GET /api/onboarding/pricing-summary`, which
 * resolves the plan from the workspace's STORED selectedOutcomes. When the
 * workspace has no usable outcomes the API returns `missing-outcomes` and
 * this page shows a recovery path to the outcomes step — it never renders a
 * fake "Free / 0 outcomes / 0 apps" summary.
 */
export function PricingSummaryPage() {
  const [status, setStatus] = useState<PageStatus>('loading');
  const [summary, setSummary] = useState<PlanSummary | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await fetch('/api/onboarding/pricing-summary', { credentials: 'include' });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        setStatus('error');
        return;
      }
      setSummary(data as PlanSummary);
      setStatus(data.status === 'missing-outcomes' ? 'missing-outcomes' : 'ready');
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const mode: 'business' | 'personal' = summary?.mode === 'personal' ? 'personal' : 'business';
  const isPersonal = mode === 'personal';

  const handleBack = async () => {
    try {
      await fetch('/api/onboarding/back', { method: 'POST', credentials: 'include' });
    } catch {
      // Ignore errors on back navigation
    }
    // Both flows place `outcomes` immediately before pricing (business R5,
    // personal R6) — Back always returns there.
    navigate(onboardingPath(mode, 'outcomes', continueUrl));
  };

  const handleContinue = async () => {
    setIsSaving(true);
    setError('');
    try {
      const response = await fetch('/api/onboarding/step', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ step: 'pricing', data: {} }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data.error || 'Failed to proceed');
        return;
      }
      // The backend is authoritative on what comes next. `completePricing`
      // applies the R4 conditional-review rule (skips review → invites when
      // there is nothing to review), so the page must navigate to the step
      // the response actually advances to. Hardcoding 'review' here was
      // what produced the pricing↔review bounce loop when reviewStepApplies
      // returned false.
      const backendStep = data?.state?.step as string | undefined;
      const nextStep = backendStep ?? (isPersonal ? 'complete' : 'review');
      navigate(onboardingPath(mode, nextStep, continueUrl));
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (status === 'loading') {
    return (
      <OnboardingLayout currentStep="pricing" onboardingMode={mode}>
        <div className="bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Preparing your plan...</p>
        </div>
      </OnboardingLayout>
    );
  }

  // The workspace has no usable outcomes — recover to the outcomes step
  // rather than show a fake 0-outcome / 0-app plan.
  if (status === 'missing-outcomes') {
    return (
      <OnboardingLayout currentStep="pricing" onboardingMode={mode}>
        <div className="bg-white shadow-lg rounded-lg p-8 text-center max-w-md mx-auto">
          <h1 className="text-lg font-semibold text-gray-900">
            Let's choose your outcomes first
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            We need to know what you want this workspace to help with before we
            can set it up. It only takes a moment.
          </p>
          <button
            type="button"
            onClick={() => navigate(onboardingPath(mode, 'outcomes', continueUrl))}
            className="mt-6 px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Choose your outcomes
          </button>
        </div>
      </OnboardingLayout>
    );
  }

  if (status === 'error') {
    return (
      <OnboardingLayout currentStep="pricing" onboardingMode={mode}>
        <div className="bg-white shadow-lg rounded-lg p-8 text-center max-w-md mx-auto">
          <h1 className="text-lg font-semibold text-gray-900">Something went wrong</h1>
          <p className="text-sm text-gray-500 mt-2">
            We couldn't prepare your plan. Your selections are safe — please try again.
          </p>
          <button
            type="button"
            onClick={() => load()}
            className="mt-6 px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Try again
          </button>
        </div>
      </OnboardingLayout>
    );
  }

  // status === 'ready' — summary is populated with real outcomes/apps.
  const selectedOutcomes = summary?.selectedOutcomes ?? [];
  const apps = summary?.apps ?? [];
  // PR-C3b — outcome-code → displayName map used to render the
  // "Activated by: {outcomes}" traceability line on each app chip. Built inline
  // (no useMemo) since the outcome list is at most a dozen entries.
  const outcomeNameByCode = new Map<string, string>(
    selectedOutcomes.map((o) => [o.code, o.displayName] as const),
  );
  const pricing = summary?.pricing ?? {
    monthlyBaseCents: 0,
    totalCredits: 0,
    trialDays: 0,
    isFree: true,
    plan: undefined,
    monthlyIncludedCredits: undefined,
  };
  // PR-C3a: plan lookup is the single source of truth for what the
  // user will actually be billed. When present, all downstream copy
  // pulls from it; when absent (legacy backend), we degrade to the
  // outcome-driven wording from earlier phases without lying.
  const plan = pricing.plan;
  const monthlyIncludedCredits =
    pricing.monthlyIncludedCredits ?? pricing.totalCredits;

  return (
    <OnboardingLayout currentStep="pricing" onboardingMode={mode}>
      <div className="bg-white shadow-lg rounded-lg p-6 sm:p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">{MODE_COPY[mode].heading}</h1>
          <p className="text-gray-500 mt-2">{MODE_COPY[mode].sub}</p>
        </div>

        <div className="max-w-xl mx-auto">
          <div className="border-2 border-blue-600 rounded-xl overflow-hidden mb-6">
            <div className="bg-blue-600 text-white px-6 py-4">
              <div className="flex items-baseline justify-between">
                <h2 className="text-lg font-semibold">
                  {summary?.workspaceName || 'Your workspace'}
                </h2>
                {pricing.trialDays > 0 && (
                  <span className="text-blue-200 text-sm">
                    {pricing.trialDays}-day free trial
                  </span>
                )}
              </div>
            </div>

            <div className="p-6 bg-white">
              {/*
                PR-C3a — the price/plan hero. The dollar figure comes
                straight from `plan.monthlyPriceCents` so what the user
                sees is exactly what completeOnboarding writes to
                `WorkspaceSubscription`. When no plan came back on the
                wire (unexpected in production), we degrade to a plain
                "no charge" heading rather than fabricating a number.
              */}
              <div className="text-center mb-6 pb-6 border-b border-gray-100" data-testid="pricing-hero">
                {plan ? (
                  <>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Your plan</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      <span data-testid="pricing-plan-name">{plan.name}</span>
                    </p>
                    <p className="text-lg text-gray-700 mt-2">
                      {plan.monthlyPriceCents === 0 ? (
                        <span data-testid="pricing-plan-price">Free · {formatDollars(0)}/mo</span>
                      ) : (
                        <>
                          <span data-testid="pricing-plan-price">
                            {formatDollars(plan.monthlyPriceCents)}/mo
                          </span>
                          {pricing.trialDays > 0 && (
                            <span className="text-sm text-gray-500 ml-2">
                              after your {pricing.trialDays}-day free trial
                            </span>
                          )}
                          {plan.annualPriceCents !== null && (
                            <span className="block text-sm text-gray-500 mt-1">
                              or {formatDollars(plan.annualPriceCents)}/year
                            </span>
                          )}
                        </>
                      )}
                    </p>
                    <p className="text-sm text-gray-500 mt-3 max-w-md mx-auto">
                      {isPersonal
                        ? `We picked ${plan.name} because it covers every outcome you selected.`
                        : `We picked ${plan.name} — our starting business tier — because it covers every outcome you selected.`}
                    </p>
                  </>
                ) : (
                  <div>
                    {/*
                      Defensive fallback when the backend doesn't return a
                      plan sub-object (pre-C3a client hitting a C3a
                      backend, or the plan-lookup silent-skip path). Shows
                      the same monthly figure the backend surfaces, no
                      plan-name headline, no misleading "Included" copy.
                    */}
                    <p className="text-2xl font-bold text-gray-900" data-testid="pricing-plan-price">
                      {pricing.isFree ? 'Free' : `${formatDollars(pricing.monthlyBaseCents)}/mo`}
                    </p>
                    {pricing.trialDays > 0 && (
                      <p className="text-sm text-gray-500 mt-1">
                        after your {pricing.trialDays}-day free trial
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Selected outcomes — plain checklist. Per-outcome $ badges
                  were retired (all outcomes are $0 today; the badges
                  implied outcome pricing that doesn't exist). */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  What you're improving ({selectedOutcomes.length})
                </h3>
                <ul className="space-y-2">
                  {selectedOutcomes.map((outcome) => (
                    <li
                      key={outcome.code}
                      className="flex items-center gap-2 text-sm text-gray-700"
                    >
                      <svg
                        className="w-4 h-4 text-green-500 flex-shrink-0"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                      {outcome.displayName}
                    </li>
                  ))}
                </ul>
              </div>

              {/*
                Apps we'll activate — heading intentionally uses the
                mechanism-accurate "activate," not "Included." Apps come
                from outcomes (via getVisibleAppsForOutcomes), not from
                the plan. See PR-C3a pricing audit for the mismatch this
                closes.

                PR-C3b — outcome → app traceability. When the user has
                selected more than one outcome, each chip carries a
                "Activated by: {outcomes}" line so the reader can see which
                selection activated it. Single-outcome flows omit the
                line (the connection is implicit — every app on the
                page came from the sole selected outcome).
              */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  Apps we'll activate ({apps.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {apps.map((app) => {
                    const traceNames =
                      selectedOutcomes.length > 1 && app.activatedByOutcomes
                        ? outcomeNamesFor(app.activatedByOutcomes, outcomeNameByCode)
                        : null;
                    return (
                      <AppInfoTooltip
                        key={app.appId}
                        description={app.description}
                        className={`px-3 py-1 bg-gray-100 rounded-full text-xs text-left leading-tight ${
                          app.description
                            ? 'hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-400'
                            : ''
                        }`}
                        testId={`pricing-app-${app.appId}`}
                      >
                        <span className="block font-medium text-gray-800">
                          {app.displayName}
                        </span>
                        {app.subtitle && (
                          <span className="block text-[10px] text-gray-500">
                            {app.subtitle}
                          </span>
                        )}
                        {traceNames && traceNames.length > 0 && (
                          <span
                            className="block text-[10px] italic text-gray-400"
                            data-testid={`pricing-app-trace-${app.appId}`}
                          >
                            Activated by: {traceNames.join(', ')}
                          </span>
                        )}
                      </AppInfoTooltip>
                    );
                  })}
                </div>
              </div>

              {/*
                Capacity included in your plan — the word "included" is
                used ONLY to describe plan capacity, never apps. Plan
                credits + outcome credits are shown separately so the
                user understands where each part comes from.
              */}
              {plan && (
                <div className="pt-6 border-t border-gray-100" data-testid="pricing-capacity">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                    Capacity included in your plan
                  </h3>
                  <dl className="space-y-2 text-sm">
                    <div className="flex items-baseline justify-between">
                      <dt className="text-gray-500">Monthly credits</dt>
                      <dd className="text-right text-gray-900">
                        <span data-testid="pricing-capacity-credits" className="font-medium">
                          {monthlyIncludedCredits.toLocaleString()}
                        </span>
                        {pricing.totalCredits > 0 && (
                          <span className="block text-xs text-gray-400">
                            {plan.baseCredits.toLocaleString()} from your plan
                            {' + '}
                            {pricing.totalCredits.toLocaleString()} from your outcomes
                          </span>
                        )}
                      </dd>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <dt className="text-gray-500">Seats</dt>
                      <dd className="text-right text-gray-900">
                        <span data-testid="pricing-capacity-seats" className="font-medium">
                          {plan.includedSeats}
                        </span>
                        {plan.perSeatCents > 0 && plan.maxSeats !== null && (
                          <span className="block text-xs text-gray-400">
                            add more for {formatDollars(plan.perSeatCents)}/seat/mo
                            {plan.maxSeats > plan.includedSeats && (
                              <> up to {plan.maxSeats}</>
                            )}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <dt className="text-gray-500">Storage</dt>
                      <dd
                        className="font-medium text-gray-900"
                        data-testid="pricing-capacity-storage"
                      >
                        {formatStorage(plan.storageLimitBytes)}
                      </dd>
                    </div>
                  </dl>
                </div>
              )}
            </div>
          </div>

          {pricing.trialDays > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex gap-3">
                <svg
                  className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="text-sm">
                  <p className="font-medium text-blue-900">
                    Your {pricing.trialDays}-day free trial
                  </p>
                  <ul className="text-blue-700 mt-1 space-y-1">
                    <li>Full access to every outcome you selected</li>
                    <li>Trial credits to get you started</li>
                    <li>No credit card required</li>
                    <li>Cancel anytime</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="max-w-xl mx-auto mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="max-w-xl mx-auto mt-8 pt-6 border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="px-6 py-2.5 text-gray-600 hover:text-gray-900 transition-colors font-medium"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={isSaving}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? 'Continuing...' : 'Continue'}
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
}
